import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import clsx from 'clsx';

import { ChatBubbleIcon } from '@/components/common/SupportChat/SupportChatIcons';
import { Button, Label } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { OfflineBlockedPopup } from '@/components/modals/OfflineBlockedPopup';
import { AuthContext, OfflineContext, ProfilePictureContext } from '@/contexts';
import { PRIVATE_PAGE_ROUTES } from '@/enums';
import { probeOnlineStatus, useCanUseSupportChat, useMobileScreen, useSidebarItems } from '@/hooks';
import { getSidebarItemKey, SidebarItem } from '@/layout';
import { OrganisationSelect } from '@/layout/Sidebar/OrganisationSelect';
import { getSidebarButtonClassName, isSidebarPathActive } from '@/layout/Sidebar/Sidebar.styles';
import { UserAvatar } from '@/pages/ProfilePage/UserAvatar';
import { LogOut, SentoaLogo, XMarkIcon as XMark } from '@/resources';
import { useGetCurrentUserQuery } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { useSupportChatStore } from '@/store/supportChat';
import { SidebarItemType, SidebarProps } from '@/types';

type SidebarHeaderProps = SidebarProps & {
  isMobileView: boolean | null;
};

type SidebarNavigationProps = {
  items: SidebarItemType[];
  pathname: string;
  openDropdown: string | null;
  navigateTo: (path: string) => void;
  setOpenDropdown: (itemKey: string | null) => void;
  isOffline?: boolean;
};

type SidebarProfileButtonProps = {
  fullName: string;
  profileImage: JSX.Element;
  roleName?: string;
  isProfileRoute: boolean;
  logout: () => void;
  navigateTo: (path: string) => void;
  isOffline?: boolean;
};

const SidebarHeader = ({ closeSidebar, isMobileView, isTabletView }: SidebarHeaderProps) =>
  isMobileView || isTabletView ? (
    <button type='button' className='m-4' onClick={closeSidebar}>
      <XMark className='size-4' />
    </button>
  ) : (
    <SentoaLogo className='mx-9 mt-9 h-[32px] w-[130px]' />
  );

const SidebarNavigation = ({
  items,
  pathname,
  openDropdown,
  navigateTo,
  setOpenDropdown,
  isOffline = false
}: SidebarNavigationProps) => (
  <div className='mt-4'>
    {items.map((sidebarItem: SidebarItemType) => {
      const itemKey = getSidebarItemKey(sidebarItem);

      return (
        <SidebarItem
          key={itemKey}
          sidebarItem={sidebarItem}
          pathname={pathname}
          onNavigate={navigateTo}
          isDropdownOpen={openDropdown === itemKey}
          setOpenDropdown={setOpenDropdown}
          isOffline={isOffline}
        />
      );
    })}
  </div>
);

const SidebarProfileButton = ({
  fullName,
  profileImage,
  roleName,
  isProfileRoute,
  logout,
  navigateTo,
  isOffline = false
}: SidebarProfileButtonProps) => {
  const [t] = useTranslation('common');
  const [showOfflinePopup, setShowOfflinePopup] = useState(false);

  return (
    <>
      <OfflineBlockedPopup isOpen={showOfflinePopup} onClose={() => setShowOfflinePopup(false)} />
      <button
        type='button'
        data-testid={TestIds.nav.profileLink}
        onClick={() => {
          if (isOffline) {
            setShowOfflinePopup(true);
          } else {
            navigateTo(PRIVATE_PAGE_ROUTES.PROFILE);
          }
        }}
        className={getSidebarButtonClassName(
          false,
          clsx(
            'group relative mb-3 !h-auto min-h-10 !items-center py-2 pr-10 !text-left !text-primary-blue',
            isOffline && 'cursor-not-allowed opacity-40'
          )
        )}>
        <div className='flex min-w-0 flex-1 flex-row items-center text-left'>
          {profileImage}

          <div className='min-w-0 flex-1 text-left text-sm leading-tight'>
            <div
              className={clsx(
                'whitespace-normal break-normal [overflow-wrap:normal] [word-break:normal]',
                isProfileRoute && 'font-bold'
              )}>
              {fullName}
            </div>

            {roleName && (
              <div className='mt-0.5 whitespace-normal break-normal text-left text-xs !font-normal leading-tight !text-secondary-steel-blue [overflow-wrap:normal] [word-break:normal]'>
                {t(roleName)}
              </div>
            )}
          </div>
        </div>
        <span data-testid={TestIds.layout.logoutButton} className='inline-flex'>
          <LogOut
            onClick={(event) => {
              event.stopPropagation();
              if (isOffline) {
                setShowOfflinePopup(true);
              } else {
                logout();
              }
            }}
            className={clsx(
              'absolute right-3 top-1/2 size-5 shrink-0 -translate-y-1/2',
              isOffline && 'cursor-not-allowed'
            )}
          />
        </span>
      </button>
    </>
  );
};

export const Sidebar = ({ closeSidebar, isTabletView }: SidebarProps) => {
  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const isMobileView = useMobileScreen();
  const sidebarItems = useSidebarItems();
  const { pathname } = useLocation();
  const { logout, currentUserData: currentUser } = useContext(AuthContext);
  const { profilePictureUrl } = useContext(ProfilePictureContext);
  const { isOnline } = useContext(OfflineContext);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const canUseSupportChat = useCanUseSupportChat();
  const setIsSupportChatOpen = useSupportChatStore((state) => state.setIsOpen);

  const { data: user, isLoading, isError } = useGetCurrentUserQuery(isOnline);

  if (!isOnline && isLoading) {
    // skip — will render with currentUser from AuthContext localStorage cache
  } else if (isLoading) {
    return <Label text={t('loading')} />;
  } else if (isError && isOnline && !currentUser) {
    return <Label text={t('errorFetchingUserData')} />;
  }

  const getIsRouteSelected = (route?: string) => isSidebarPathActive(pathname, route);

  const navigateTo = (path: string) => {
    navigate(path);
    closeSidebar?.();
  };

  const openSupportChat = () => {
    setIsSupportChatOpen(true);
    closeSidebar?.();
  };

  const handleReconnect = async () => {
    setIsCheckingConnection(true);
    try {
      const isReachable = await probeOnlineStatus();
      if (isReachable) {
        window.location.reload();
      }
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const getProfileImage = () => {
    return profilePictureUrl ? (
      <img
        src={profilePictureUrl}
        alt={t('profilePicture')}
        className='mr-2 size-8 rounded-full object-cover'
      />
    ) : (
      <UserAvatar
        style={clsx(
          'mr-2 size-8 text-sm font-medium',
          isProfileRoute
            ? 'border-2 border-primary-blue text-primary-blue'
            : 'border border-primary-blue hover:text-primary-blue'
        )}
      />
    );
  };

  const isProfileRoute = getIsRouteSelected('/profile');
  const fullName =
    `${currentUser?.firstName ?? user?.data?.firstName ?? ''} ${currentUser?.lastName ?? user?.data?.lastName ?? ''}`.trim();

  return (
    <aside
      data-testid={TestIds.nav.sidebar}
      className={clsx(
        'left-0 top-0 z-30 flex flex-col bg-primary-gray',
        isMobileView || isTabletView ? 'absolute h-dvh w-[65%] sm:w-1/2 md:w-60' : 'sticky h-screen'
      )}>
      <SidebarHeader
        closeSidebar={closeSidebar}
        isMobileView={isMobileView}
        isTabletView={isTabletView}
      />
      <div className='mt-8 flex flex-1 flex-col justify-between md:mt-12'>
        {currentUser?.organizations.length === 1 ? null : <OrganisationSelect />}
        {!isOnline && (
          <div className='mx-4 mt-2 max-w-[220px] rounded-lg bg-amber-100 px-3 py-2 text-xs font-medium text-amber-800'>
            <p>{t('offlineModeBanner')}</p>
            <Button
              type='button'
              variant={ButtonVariant.TERTIARY}
              handleOnClick={handleReconnect}
              disabled={isCheckingConnection}
              className='mt-2 rounded-md bg-amber-200 px-2 py-1 text-2xs font-semibold text-amber-900 hover:bg-amber-300'>
              {isCheckingConnection ? t('loading') : t('tryReconnect')}
            </Button>
          </div>
        )}
        <hr className='mx-auto mt-4 w-3/4' />
        <nav className='mx-4 flex h-full flex-col justify-between'>
          <SidebarNavigation
            items={sidebarItems}
            pathname={pathname}
            openDropdown={openDropdown}
            navigateTo={navigateTo}
            setOpenDropdown={setOpenDropdown}
            isOffline={!isOnline}
          />
          <div>
            {/* Phones have no floating chat launcher, so the chat opens from here. */}
            {isMobileView && canUseSupportChat && (
              <button
                type='button'
                data-testid={TestIds.supportChat.menuItem}
                onClick={openSupportChat}
                className={getSidebarButtonClassName(false)}>
                <ChatBubbleIcon className='mr-4 size-5' />
                <div>{t('help')}</div>
              </button>
            )}
            <SidebarProfileButton
              fullName={fullName}
              profileImage={getProfileImage()}
              roleName={currentUser?.role?.name}
              isProfileRoute={isProfileRoute}
              logout={logout}
              navigateTo={navigateTo}
              isOffline={!isOnline}
            />
          </div>
        </nav>
      </div>
    </aside>
  );
};
